import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const GRAD = 'linear-gradient(100deg,#3B3CC4 0%,#7C2BD0 30%,#C81E73 55%,#E51E50 72%,#F26A2C 100%)';

// ── Flows: 'login' | 'forgot' ─────────────────────────────────────────────────
export default function AdminLogin() {
  const navigate = useNavigate();

  // LOGIN flow state
  const [loginStep, setLoginStep] = useState(1);   // 1 = credentials, 2 = OTP
  const [form, setForm] = useState({ email: '', password: '', otp: '' });
  const [maskedEmail, setMaskedEmail] = useState('');

  // FORGOT flow state
  const [flow, setFlow] = useState('login');        // 'login' | 'forgot'
  const [resetStep, setResetStep] = useState(1);    // 1 = username, 2 = otp+newpwd
  const [resetForm, setResetForm] = useState({ username: '', otp: '', newPassword: '', confirmPassword: '' });
  const [resetMsg, setResetMsg] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleResetChange = (e) => setResetForm({ ...resetForm, [e.target.name]: e.target.value });

  // ── LOGIN: step 1 — credentials ────────────────────────────────────────────
  async function handleCredentials(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');
      // DEV MODE: token returned directly, skip OTP step
      if (data.token) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        navigate('/admin/dashboard');
        return;
      }
      setMaskedEmail(data.email);
      setLoginStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // ── LOGIN: step 2 — OTP ────────────────────────────────────────────────────
  async function handleOTP(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, otp: form.otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'OTP verification failed');
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      navigate('/admin/dashboard');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // ── FORGOT: step 1 — request OTP ──────────────────────────────────────────
  async function handleForgotRequest(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetForm.username }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed');
      setResetMsg(data.message);
      setResetStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // ── FORGOT: step 2 — submit OTP + new password ─────────────────────────────
  async function handleResetPassword(e) {
    e.preventDefault();
    setError('');
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      return setError('Passwords do not match.');
    }
    if (resetForm.newPassword.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username:    resetForm.username,
          otp:         resetForm.otp,
          newPassword: resetForm.newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Reset failed');
      setSuccess(data.message);
      // Return to login after 2 seconds
      setTimeout(() => {
        setFlow('login'); setResetStep(1);
        setResetForm({ username: '', otp: '', newPassword: '', confirmPassword: '' });
        setSuccess(''); setError('');
      }, 2500);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  function switchToForgot() {
    setFlow('forgot'); setError(''); setSuccess('');
    setResetStep(1); setResetForm({ username: '', otp: '', newPassword: '', confirmPassword: '' });
  }

  function switchToLogin() {
    setFlow('login'); setLoginStep(1); setError(''); setSuccess('');
    setForm({ email: '', password: '', otp: '' });
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.container}>
      <style>{CSS}</style>
      <div className="aclg-grid" />
      <div className="aclg-glow g1" />
      <div className="aclg-glow g2" />
      <div style={s.card}>
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="LavanAI"
          onError={e => { e.currentTarget.replaceWith(Object.assign(document.createElement('div'), { textContent: flow === 'forgot' ? '🔑' : '⚡', style: 'font-size:36px;margin-bottom:8px' })); }}
          style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', margin: '0 auto 12px', display: 'block', boxShadow: '0 6px 20px rgba(0,0,0,0.4)' }} />
        <h2 style={s.title}>{flow === 'forgot' ? 'Reset Password' : 'Admin Login'}</h2>
        <p style={s.subtitle}>Lavan AI</p>

        {error   && <div style={s.error}>{error}</div>}
        {success && <div style={s.successBox}>{success}</div>}

        {/* ── LOGIN FLOW ─────────────────────────────────────────────────── */}
        {flow === 'login' && loginStep === 1 && (
          <form onSubmit={handleCredentials}>
            <input style={s.input} type="email" name="email" placeholder="Email address"
              value={form.email} onChange={handleChange} required autoFocus />
            <div style={s.pwWrap}>
              <input style={{ ...s.input, paddingRight: 44, marginBottom: 0 }} type={showPassword ? 'text' : 'password'} name="password" placeholder="Password"
              value={form.password} onChange={handleChange} required />
              <button type="button" style={s.eyeBtn} onClick={() => setShowPassword(v => !v)}>{showPassword ? '🙈' : '👁️'}</button>
            </div>
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Continue →'}
            </button>
            <button style={s.linkBtn} type="button" onClick={switchToForgot}>
              Forgot password?
            </button>
          </form>
        )}

        {flow === 'login' && loginStep === 2 && (
          <form onSubmit={handleOTP}>
            <p style={s.otpInfo}>OTP sent to <strong>{maskedEmail}</strong></p>
            <p style={s.otpTimer}>⏱ Expires in 3 minutes</p>
            <input
              style={{ ...s.input, textAlign: 'center', fontSize: 28, letterSpacing: 10 }}
              type="text" name="otp" placeholder="000000" maxLength={6}
              value={form.otp} onChange={handleChange} required autoFocus
            />
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button style={s.btnBack} type="button"
              onClick={() => { setLoginStep(1); setError(''); setForm({ ...form, otp: '' }); }}>
              ← Back
            </button>
          </form>
        )}

        {/* ── FORGOT FLOW ────────────────────────────────────────────────── */}
        {flow === 'forgot' && resetStep === 1 && (
          <form onSubmit={handleForgotRequest}>
            <p style={s.helpText}>Enter your admin username and we'll send a reset code to your registered email.</p>
            <input style={s.input} type="text" name="username" placeholder="Admin username"
              value={resetForm.username} onChange={handleResetChange} required autoFocus />
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button style={s.linkBtn} type="button" onClick={switchToLogin}>
              ← Back to Login
            </button>
          </form>
        )}

        {flow === 'forgot' && resetStep === 2 && (
          <form onSubmit={handleResetPassword}>
            <p style={s.otpInfo}>{resetMsg}</p>
            <p style={s.otpTimer}>⏱ Code expires in 10 minutes</p>
            <input
              style={{ ...s.input, textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
              type="text" name="otp" placeholder="Reset code" maxLength={6}
              value={resetForm.otp} onChange={handleResetChange} required autoFocus
            />
            <div style={s.pwWrap}>
              <input style={{ ...s.input, paddingRight: 44, marginBottom: 0 }} type={showNewPw ? 'text' : 'password'} name="newPassword" placeholder="New password (min 8 chars)"
              value={resetForm.newPassword} onChange={handleResetChange} required />
              <button type="button" style={s.eyeBtn} onClick={() => setShowNewPw(v => !v)}>{showNewPw ? '🙈' : '👁️'}</button>
            </div>
            <div style={s.pwWrap}>
              <input style={{ ...s.input, paddingRight: 44, marginBottom: 0 }} type={showConfirmPw ? 'text' : 'password'} name="confirmPassword" placeholder="Confirm new password"
              value={resetForm.confirmPassword} onChange={handleResetChange} required />
              <button type="button" style={s.eyeBtn} onClick={() => setShowConfirmPw(v => !v)}>{showConfirmPw ? '🙈' : '👁️'}</button>
            </div>
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button style={s.btnBack} type="button"
              onClick={() => { setResetStep(1); setError(''); }}>
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  container:  { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0B0D14', padding: '16px', position: 'relative', overflow: 'hidden' },
  card:       { position: 'relative', zIndex: 1, background: '#151823', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 'clamp(26px,5vw,40px) clamp(20px,5vw,36px)', width: 400, maxWidth: '92vw', boxShadow: '0 24px 60px rgba(0,0,0,0.55)', textAlign: 'center' },
  logo:       { fontSize: 36, marginBottom: 8 },
  title:      { color: '#e6edf3', fontSize: 22, margin: '0 0 4px' },
  subtitle:   { color: '#8b949e', fontSize: 13, marginBottom: 28 },
  input:      { display: 'block', width: '100%', padding: '12px 14px', marginBottom: 14, background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, color: '#e6edf3', fontSize: 15, boxSizing: 'border-box', outline: 'none' },
  btn:        { width: '100%', padding: 13, background: GRAD, border: 'none', borderRadius: 8, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10, boxShadow: '0 8px 22px rgba(124,43,208,0.38)' },
  btnBack:    { width: '100%', padding: 11, background: 'transparent', border: '1px solid #30363d', borderRadius: 8, color: '#8b949e', fontSize: 14, cursor: 'pointer' },
  linkBtn:    { background: 'none', border: 'none', color: '#C4B5FD', fontSize: 13, cursor: 'pointer', marginTop: 6, textDecoration: 'underline' },
  error:      { background: '#da363322', border: '1px solid #da3633', color: '#f85149', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, textAlign: 'left' },
  successBox: { background: '#23863622', border: '1px solid #238636', color: '#3fb950', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 },
  otpInfo:    { color: '#ccc', marginBottom: 4, fontSize: 14 },
  otpTimer:   { color: '#f59e0b', marginBottom: 20, fontSize: 12 },
  helpText:   { color: '#8b949e', fontSize: 13, marginBottom: 20, textAlign: 'left', lineHeight: 1.5 },
  pwWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  eyeBtn: { position: 'absolute', right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#8b949e', padding: '0 4px', lineHeight: 1, top: '50%', transform: 'translateY(-50%)' },
};

// Same glow + grid background as the user login
const CSS = `
.aclg-grid{position:absolute;inset:0;
  background-image:linear-gradient(rgba(124,43,208,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(124,43,208,0.05) 1px,transparent 1px);
  background-size:48px 48px;
  -webkit-mask-image:radial-gradient(circle at 50% 42%,#000 0%,transparent 78%);
  mask-image:radial-gradient(circle at 50% 42%,#000 0%,transparent 78%);}
.aclg-glow{position:absolute;border-radius:50%;filter:blur(90px);opacity:.5;pointer-events:none;}
.aclg-glow.g1{width:520px;height:520px;background:radial-gradient(circle,#5B2BD6,transparent 65%);top:-15%;left:-10%;}
.aclg-glow.g2{width:480px;height:480px;background:radial-gradient(circle,#E51E50,transparent 65%);bottom:-18%;right:-8%;opacity:.4;}
`;
